import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import { useHolidaySavingsAmount } from "../hooks/useHolidaySavingsAmount";
import { EmployeeHolidaySavingsRecord } from "../api/salary/holiday-savings.service";
import { formatAmount, formatDate } from "../helper/common.helper";
import "./holiday-pay-calendar.scss";
import LoadingCard from '../components/SkeletonLoading'
import { EmptyState } from "./dashboard/EmptyState";

/* Register the Swedish locale with react-datepicker once at module
   load — `locale="sv"` then swaps month names, weekday headers
   and screen-reader strings to Swedish. Same idiom as every other
   themed picker in the app. */
registerLocale("sv", sv);

/* Portal the calendar popper to <body> so it escapes the filter
   bar's overflow / transform context and is never clipped. Paired
   with `popperProps.strategy: 'fixed'` for viewport-relative
   positioning. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    ReactDOM.createPortal(children, document.body);

/* String <-> Date bridges. handleFromDateSelect / handleToDateSelect
   stay string-only (yyyy-mm-dd ISO) so every downstream call —
   appliedFilters, fetchHolidaySavingsAmount, etc — is unchanged. */
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
export const HolidayInvoiceHistoryModal = () => {
    const { t, i18n } = useTranslation();
    /* Re-evaluates whenever i18next switches language; the picker
       re-renders with Swedish month/weekday labels in sv-se and
       falls back to library default (English) otherwise. */
    const calendarLocale = (i18n.language || "").toLowerCase().startsWith("sv") ? "sv" : undefined;
    const isMobile = window.innerWidth <= 768;
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: '',
        toDate: '',
        status: ''
    });

    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });
    const {
        fetchHolidaySavingsAmount,
        holidaySavingsRecords,
        loading,
        error: hookError,
        currentPage,
        totalPages,
        goToNextPage,
        goToPreviousPage,
    } = useHolidaySavingsAmount(10);


    const handleFromDateSelect = (date: string) => {
        setFromDate(date);
        setToolTipError({ ...toolTipError, fromDateNotSelected: false, invalidFromdate: false });
    };

    const handleToDateSelect = (date: string) => {
        setToDate(date);
        setToolTipError({ ...toolTipError, toDateNotSelected: false, invalidFromdate: false });
    };

    const handleClearFilters = () => {
        setFromDate('');
        setToDate('');
        setStatus('');
        setAppliedFilters({
            fromDate: '',
            toDate: '',
            status: ''
        });
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });
    };

    // Fetch data on component mount and when filters change
    useEffect(() => {
        fetchHolidaySavingsAmount(fromDate, toDate);
    }, [fromDate, toDate, fetchHolidaySavingsAmount]);
    return (
        <div className="invoice-table-container">

            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('HOLIDAY_TRANSACTION_HISTORY')}</h3>
                <div className="holiday-filter-section">
                    <div className="holiday-filter-inputs">
                        <div className="holiday-filter-input">
                            <DatePicker
                                selected={parseIso(fromDate)}
                                onChange={(d: Date | null) => handleFromDateSelect(toIso(d))}
                                dateFormat="yyyy/MM/dd"
                                placeholderText={t('From')}
                                className="holiday-filter__input"
                                wrapperClassName="holiday-filter__input-dp"
                                popperContainer={PopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="holiday-filter__dp-popper"
                                calendarClassName="holiday-filter__dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                locale={calendarLocale}
                                autoComplete="off"
                                maxDate={new Date()}
                            />
                            {toolTipError.fromDateNotSelected && (
                                <div className="tooltip-error">
                                    {t('PLEASE_SELECT_FROM_DATE')}
                                </div>
                            )}
                            {toolTipError.invalidFromdate && (
                                <div className="tooltip-error">
                                    {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                </div>
                            )}
                        </div>
                        <div className="holiday-filter-input">
                            <DatePicker
                                selected={parseIso(toDate)}
                                onChange={(d: Date | null) => handleToDateSelect(toIso(d))}
                                dateFormat="yyyy/MM/dd"
                                placeholderText={t('To')}
                                className="holiday-filter__input"
                                wrapperClassName="holiday-filter__input-dp"
                                popperContainer={PopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="holiday-filter__dp-popper"
                                calendarClassName="holiday-filter__dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                locale={calendarLocale}
                                autoComplete="off"
                                maxDate={new Date()}
                                minDate={parseIso(fromDate) || undefined}
                            />
                            {toolTipError.toDateNotSelected && (
                                <div className="tooltip-error">
                                    {t('PLEASE_SELECT_TO_DATE')}
                                </div>
                            )}
                        </div>
                        <button className="holiday-filter-clear-btn"
                            onClick={handleClearFilters}
                        >
                            {t('Clear')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="invoice-table-content">
                {loading && (
                                <div style={{display: "flex"}}>
                                <LoadingCard/></div>
                            )}
                {!loading && !error && !hookError && holidaySavingsRecords && holidaySavingsRecords.length >= 0 && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID')}</TableColumn>
                                <TableColumn>{t('INVOICE_NO')}</TableColumn>
                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {isMobile ? (
                                holidaySavingsRecords?.map((record: EmployeeHolidaySavingsRecord) => (
                                    <MobileCardWrapper key={record.invoiceId || Math.random()}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{record.invoiceId || 'N/A'}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NO')}>
                                            {record.invoiceNumber ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            {formatAmount(record.holidaySavingsAmount) ?? 'N/A'} {t('kr')}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {formatDate(record.createdDate) ?? 'N/A'}
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                holidaySavingsRecords.map((record: EmployeeHolidaySavingsRecord) => (
                                    <TableRow key={record.invoiceId || Math.random()}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{record.invoiceId || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell>{record.invoiceNumber ?? 'N/A'}</TableCell>
                                        <TableCell>{formatAmount(record.holidaySavingsAmount) ?? 'N/A'} {t('kr')}</TableCell>
                                        <TableCell>{formatDate(record.createdDate) ?? 'N/A'}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}

                {/* Empty state — full width, outside the table */}
                {!loading && !error && !hookError && holidaySavingsRecords.length === 0 && (
                    <EmptyState
                        title={t('NO_DATA_FOUND')}
                        subtitle={t('NO_DATA_SUBTITLE')}
                    />
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '1rem' }}>
                        <button
                            onClick={goToPreviousPage}
                            disabled={currentPage === 1}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: currentPage === 1 ? '#ccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {t('PREVIOUS')}
                        </button>
                        <span style={{ margin: '0 1rem' }}>
                            {t('PAGE')} {currentPage} {t('OF')} {totalPages}
                        </span>
                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: currentPage === totalPages ? '#ccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {t('NEXT')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}